
import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, MoreVertical, User, AlertTriangle } from "lucide-react";
import { useChatMessages } from "@/hooks/useChats";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
  type: 'text' | 'system';
}

interface ChatScreenProps {
  chatId: string;
  partnerName: string;
  partnerAvatar?: string;
  partnerId?: string;
  onBack: () => void;
  onViewProfile?: (userId: string) => void;
}

const ChatScreen = ({ chatId, partnerName, partnerAvatar, partnerId, onBack, onViewProfile }: ChatScreenProps) => {
  const [newMessage, setNewMessage] = useState('');
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const { user } = useAuth();
  const { messages: chatMessages, loading, sendMessage, error } = useChatMessages(chatId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Listen to visualViewport resize to detect keyboard open/close
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      const offset = window.innerHeight - viewport.height;
      setKeyboardOffset(offset > 50 ? offset : 0);
    };

    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);
    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, []);

  // Auto-scroll to bottom when keyboard opens or new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages.length, keyboardOffset]);

  const handleInputFocus = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  }, []);
  
  // Transform Supabase messages to component format
  const messages: Message[] = chatMessages.map(msg => ({
    id: msg.id,
    senderId: msg.sender_id === user?.id ? 'me' : 'partner',
    senderName: msg.sender_id === user?.id ? 'Yo' : (msg.sender?.full_name || partnerName),
    content: msg.message,
    timestamp: msg.created_at,
    type: 'text' as const
  }));

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      try {
        await sendMessage(newMessage.trim());
        setNewMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading && chatMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full" style={{ paddingBottom: keyboardOffset > 0 ? `${keyboardOffset}px` : undefined }}>
      {/* Chat Header */}
      <Card className="bg-white/90 backdrop-blur-sm border-b rounded-b-none">
        <CardHeader className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">
                {partnerAvatar || partnerName.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <h2 className="font-bold text-gray-900">{partnerName}</h2>
                <p className="text-sm text-green-600">En línea</p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem 
                  onClick={() => partnerId && onViewProfile?.(partnerId)}
                  disabled={!partnerId}
                >
                  <User className="h-4 w-4 mr-2" />
                  Ver perfil
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    toast.info("Función de reportar/bloquear próximamente disponible");
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Reportar / Bloquear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
      </Card>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 overscroll-contain">
        {messages.map((message, index) => {
          const showDate = index === 0 || 
            formatDate(message.timestamp) !== formatDate(messages[index - 1].timestamp);
          
          return (
            <div key={message.id}>
              {showDate && (
                <div className="text-center">
                  <span className="bg-white px-3 py-1 rounded-full text-xs text-gray-500 border">
                    {formatDate(message.timestamp)}
                  </span>
                </div>
              )}
              
              <div className={`flex ${message.senderId === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] ${message.senderId === 'me' ? 'order-2' : 'order-1'}`}>
                  <div
                    className={`px-4 py-2 rounded-lg ${
                      message.senderId === 'me'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white text-gray-900 border'
                    }`}
                  >
                    <p>{message.content}</p>
                  </div>
                  <p className={`text-xs text-gray-500 mt-1 ${
                    message.senderId === 'me' ? 'text-right' : 'text-left'
                  }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <Card className="bg-white/90 backdrop-blur-sm border-t rounded-t-none shrink-0" style={{ paddingBottom: keyboardOffset > 0 ? '4px' : 'env(safe-area-inset-bottom, 0px)' }}>
        <CardContent className="p-3">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder="Escribe un mensaje..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={handleInputFocus}
              className="flex-1"
            />
            <Button 
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || loading}
              className="bg-primary hover:bg-primary/90"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatScreen;
